import { useMemo, useState } from 'react'
import {
  CheckIcon,
  CheckCircleIcon,
  ClockIcon,
  PencilSquareIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'

import {
  Badge,
  Button,
  Card,
  CardContent,
  Input,
  Label,
  Progress,
  Spinner,
  Textarea,
} from '../../components/ui'
import { cn } from '../../shared/lib/cn'
import { useLagAccrualRun } from './api'
import type { CategorySummary, HitlActionType, HitlDecision } from './types'
import { categoryTone, completionPercent, formatCurrency, formatPercent } from './utils'

function actionTone(action: HitlActionType): string {
  switch (action) {
    case 'approve':
      return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
    case 'modify':
      return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
    case 'reject':
      return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
  }
}

function categoryCopy(category: CategorySummary): string {
  if (category.id === 'legal') {
    return 'Confirm GC hours before approval. The Wilson Sonsini IP filing is the primary spike driver.'
  }
  if (category.id === 'events') {
    return 'Validate unposted events and the Cavallo Point offsite because it exceeds the single-event review limit.'
  }
  if (category.id === 'marketing') {
    return 'Confirm campaign elapsed percentages and new Content Agency activity before posting.'
  }
  return 'Check unsubmitted card balances and receipt status for conference-heavy sales activity.'
}

function ReviewCard({
  category,
  decision,
  onDecide,
}: {
  category: CategorySummary
  decision?: HitlDecision
  onDecide: (categoryId: string, decision: HitlDecision) => void
}) {
  const [amount, setAmount] = useState(category.amount)
  const [comment, setComment] = useState('')
  const [legalHours, setLegalHours] = useState('')

  const decide = (action: HitlActionType) => {
    onDecide(category.id, {
      action,
      amount,
      comment,
      legalHours: category.id === 'legal' && legalHours ? Number(legalHours) : undefined,
      decidedAt: new Date().toLocaleString(),
    })
  }

  return (
    <Card className={cn('overflow-hidden', decision && 'border-eliza-red/30')}>
      <CardContent className="p-0">
        <div className="grid gap-0 xl:grid-cols-[minmax(0,1fr)_360px]">
          <div className="p-5">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="font-subtitle text-xl text-charcoal dark:text-gray-100">
                    {category.name}
                  </h2>
                  <span className={cn('inline-flex rounded-full px-2.5 py-1 text-xs font-medium', categoryTone(category))}>
                    {category.status === 'hitl_required' ? 'HITL' : 'Auto'}
                  </span>
                </div>
                <p className="mt-2 max-w-2xl text-sm leading-6 text-gray-500 dark:text-gray-400">
                  {categoryCopy(category)}
                </p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-semibold text-charcoal dark:text-gray-100">
                  {formatCurrency(category.amount)}
                </p>
                <p className={cn(
                  'mt-1 text-sm font-medium',
                  Math.abs(category.variance_pct) > 0.3
                    ? 'text-red-600 dark:text-red-400'
                    : 'text-gray-500 dark:text-gray-400',
                )}>
                  {formatPercent(category.variance_pct)} vs avg
                </p>
              </div>
            </div>

            <div className="mt-5 grid gap-4 md:grid-cols-3">
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Debit
                </p>
                <p className="mt-1 font-mono text-sm text-charcoal dark:text-gray-100">
                  {category.debit_account} {category.debit_account_name}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Credit
                </p>
                <p className="mt-1 font-mono text-sm text-charcoal dark:text-gray-100">
                  {category.credit_account} {category.credit_account_name}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Trigger
                </p>
                <p className="mt-1 text-sm text-charcoal dark:text-gray-100">
                  {category.hitl_reason ?? 'Within policy threshold'}
                </p>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-200 bg-gray-50 p-5 dark:border-dark-border dark:bg-dark-surface-2 xl:border-l xl:border-t-0">
            <div className="grid gap-4">
              {category.id === 'legal' ? (
                <div className="grid gap-2">
                  <Label htmlFor={`${category.id}-legal-hours`}>GC-confirmed hours</Label>
                  <Input
                    id={`${category.id}-legal-hours`}
                    type="number"
                    value={legalHours}
                    placeholder="Enter hours"
                    onChange={event => setLegalHours(event.target.value)}
                  />
                </div>
              ) : null}

              <div className="grid gap-2">
                <Label htmlFor={`${category.id}-amount`}>Reviewed amount</Label>
                <Input
                  id={`${category.id}-amount`}
                  type="number"
                  value={amount}
                  onChange={event => setAmount(Number(event.target.value))}
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor={`${category.id}-comment`}>Controller comment</Label>
                <Textarea
                  id={`${category.id}-comment`}
                  value={comment}
                  placeholder="Add decision notes..."
                  onChange={event => setComment(event.target.value)}
                  rows={3}
                />
              </div>

              <div className="grid gap-2 sm:grid-cols-3">
                <Button type="button" variant="secondary" size="sm" onClick={() => decide('approve')}>
                  <CheckIcon className="h-4 w-4" />
                  Approve
                </Button>
                <Button type="button" variant="secondary" size="sm" onClick={() => decide('modify')}>
                  <PencilSquareIcon className="h-4 w-4" />
                  Modify
                </Button>
                <Button type="button" variant="destructive" size="sm" onClick={() => decide('reject')}>
                  <XMarkIcon className="h-4 w-4" />
                  Reject
                </Button>
              </div>

              {decision ? (
                <div className={cn('rounded-lg px-3 py-2 text-sm', actionTone(decision.action))}>
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-medium capitalize">{decision.action}</span>
                    <span className="text-xs">{decision.decidedAt}</span>
                  </div>
                  <p className="mt-1">
                    {formatCurrency(decision.amount)}
                    {decision.legalHours ? ` | GC hours: ${decision.legalHours}` : ''}
                  </p>
                  {decision.comment ? <p className="mt-1 text-xs">{decision.comment}</p> : null}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

export default function HitlReviewPage() {
  const { data: run, isLoading, error } = useLagAccrualRun()
  const [decisions, setDecisions] = useState<Record<string, HitlDecision>>({})

  const percent = useMemo(
    () => completionPercent(run?.categories ?? [], decisions),
    [decisions, run?.categories],
  )

  if (isLoading) {
    return (
      <div className="flex min-h-[360px] items-center justify-center">
        <Spinner size="lg" />
      </div>
    )
  }

  if (error || !run) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-red-600 dark:text-red-400">
          Unable to load HITL review data.
        </CardContent>
      </Card>
    )
  }

  const complete = percent === 100

  return (
    <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
      <div className="grid gap-4">
        {run.categories.map(category => (
          <ReviewCard
            key={category.id}
            category={category}
            decision={decisions[category.id]}
            onDecide={(categoryId, decision) => {
              setDecisions(prev => ({ ...prev, [categoryId]: decision }))
            }}
          />
        ))}
      </div>

      <aside className="xl:sticky xl:top-6 xl:self-start">
        <Card>
          <CardContent className="p-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Review completion
                </p>
                <p className="mt-2 text-3xl font-semibold text-charcoal dark:text-gray-100">
                  {percent}%
                </p>
              </div>
              {complete ? (
                <CheckCircleIcon className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
              ) : (
                <ClockIcon className="h-6 w-6 text-amber-600 dark:text-amber-400" />
              )}
            </div>
            <Progress value={percent} className="mt-5" />

            <div className="mt-6 grid gap-3">
              {run.categories.map(category => (
                <div key={category.id} className="flex items-center justify-between gap-3 text-sm">
                  <span className="text-gray-600 dark:text-gray-300">{category.name}</span>
                  {decisions[category.id] ? (
                    <Badge className={actionTone(decisions[category.id].action)}>
                      {decisions[category.id].action}
                    </Badge>
                  ) : (
                    <Badge variant="secondary">Pending</Badge>
                  )}
                </div>
              ))}
            </div>

            <div className={cn(
              'mt-6 rounded-lg px-4 py-3 text-sm',
              complete
                ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
                : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
            )}>
              {complete
                ? 'All HITL actions are complete. NetSuite load files are ready for upload.'
                : 'Complete all four category decisions before uploading the NetSuite files.'}
            </div>
          </CardContent>
        </Card>
      </aside>
    </div>
  )
}
